import { FC, Fragment, useState } from "react";
// i18next
import { Trans, useTranslation } from "react-i18next";
// Hooks
import useToggle from "app/hooks/useToggle";
import useAccountStatus from "app/hooks/useAccountStatus";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Models
import { RootState } from "app/store";
import { ICalendarEventReminder } from "app/models/CalendarEvent";
// Types
import UserRoles from "app/types/UserRoles";
// Async
import { deleteCalendarEvent } from "app/store/CalendarEvents/CalendarEventsAsync";
// Selectors
import { selectCalendarEventById } from "app/store/CalendarEvents/CalendarEventsSelectors";
import { selectCurrentAccount } from "app/store/Accounts/Accounts.selectors";
import { selectMyUser } from "app/store/Users/Users.selectors";
// MUI
import { Box, Popover, Theme, Tooltip, Typography } from "@mui/material";
// Icons
import {
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  Close as CloseIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  PeopleOutlined as PeopleOutlinedIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  PersonOutlined as PersonOutlinedIcon
} from '@mui/icons-material';
// Components
import { IconButton } from "app/components/Mui";
import ConfirmationDialog from "app/dialogs/ConfirmationDialog";
import EventsFormDialog from "./EventsFormDialog";
// Utilities
import { formatEventDateRange } from "app/utilities/DateTime";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  eventId: string | number | null;
}

const EventPopover:FC<Props> = ({ anchorEl, onClose, eventId }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { isSuspended } = useAccountStatus();

  const open = Boolean(anchorEl);

  const myUser = useAppSelector(selectMyUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const event = useAppSelector((state:RootState) => selectCalendarEventById(state, { eventId:Number(eventId) } ));

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { open:openEditDialog, toggle:toggleEditDialog } = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();

  const handleConfirm = () => {
    if (!eventId) return;
    setIsLoading(true);
    dispatch(deleteCalendarEvent(Number(eventId))).unwrap()
      .finally(() => {
        setIsLoading(false);
        if (openConfirmationDialog) toggleConfirmationDialog();
      });
  }

  const getReminderLabel = (reminder:ICalendarEventReminder) => {
    const { durationValue, durationUnit } = reminder;
    const key = `${durationUnit}${durationValue > 1 ? 's' : ''}`;

    return `${durationValue} ${t(`labels.${key}`)} ${t('labels.before')}`;
  }

  const timeFormat = currentAccount?.settings?.timeFormat || 'hh:mm a';
  const myUserRoleStaff = myUser?.role === UserRoles.Staff;

  const actionButtons = (
    <Fragment>
      {!myUserRoleStaff || event?.owner.id === myUser?.id ? (
        <Fragment>
          <Tooltip title={t('buttons.edit')}>
            <IconButton
              name="edit-event"
              size="small"
              onClick={toggleEditDialog}
              disabled={isSuspended}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('buttons.delete')}>
            <IconButton
              name="delete-event"
              size="small"
              onClick={toggleConfirmationDialog}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Fragment>
      ) : null}
      <Tooltip title={t('buttons.close')}>
        <IconButton
          name="close-popover"
          size="small"
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Fragment>
  )

  if (!open || !event) return null;
  return (
    <Fragment>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300, maxWidth: 600 }}>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
              <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
                {event.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {actionButtons}
              </Box>
            </Box>
            {event.type ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: event.type.color }} />
                <Typography variant="body1">{event.type.name}</Typography>
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarMonthOutlinedIcon fontSize="small" />
              <Typography variant="body2" color="text.secondary">{formatEventDateRange(event.start, event.end, event.allDay, timeFormat)}</Typography>
            </Box>
            {event.location ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOnOutlinedIcon fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{event.location}</Typography>
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonOutlinedIcon fontSize="small" />
              <Typography variant="body2">{event.owner.name}</Typography>
            </Box>
            {event.attendees && event.attendees.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <PeopleOutlinedIcon fontSize="small" />
                <Typography variant="body2">{event.attendees.map(a => a.name).join(', ')}</Typography>
              </Box>
            ) : null}
            {event.case ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ color: (theme:Theme) => theme.palette.primary.main, fontWeight: 500 }}>
                  {`${t('labels.case')}: `}
                </Typography>
                <Typography
                  variant="body2"
                  component="a"
                  href={`${window.location.origin}/admin/cases/${event.case.id}`}
                  target="_blank" rel="noopener noreferrer"
                  sx={{
                    textDecoration: 'none', color: 'text.primary',
                    transition: 'color 0.3s',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                >
                  {event.case.name}
                </Typography>
              </Box>
            ) : null}
            {event.description ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'justify' }}>
                {event.description}
              </Typography>
            ) : null}
            {event.reminders?.map((reminder) => (
              <Box
                key={`${reminder.durationUnit}-${reminder.durationValue}${reminder.time ? `-${reminder.time}` : ''}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <NotificationsOutlinedIcon fontSize="small" />
                <Typography
                  variant="body2" color="text.secondary"
                  sx={{ textTransform: 'lowercase' }}
                >
                  {getReminderLabel(reminder)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>

      {openEditDialog ? (
        <EventsFormDialog open={open} onClose={toggleEditDialog} event={event} />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          title={t('dialogs.confirmation.deleteTitle', { dataType: t('labels.event') })}
          loading={isLoading}
          content={(
            <Trans
              t={t} i18nKey="dialogs.confirmation.deleteConfirmation"
              components={{ strong: <strong /> }}
              values={{ dataType: t('labels.event') }}
            />
          )}
          onConfirm={handleConfirm}
        />
      ) : null}
    </Fragment>
  )
}

export default EventPopover;